import React from 'react';
import { TENANT_ROUTES } from '@/constants/routes';
import type { VisitErrorItem } from '@/types/visitClinic.types';

export interface VisitErrorMessageListProps {
  errorItems?: VisitErrorItem[] | null;
  errorMessages?: string | string[] | null;
  tenant: string | undefined;
  className?: string;
  /**
   * Text to show for the locked-period error (code visits_already_is_locked_error). 施術入力
   * passes the BD 1.3.x #24 wording; 訪問入力 leaves it unset and keeps the HIT-6081 text.
   */
  lockedPeriodMessage?: string;
}

const LINK_CLASS = 'text-blue-600 underline hover:text-blue-700';
const PLAIN_CLASS = 'text-red-600';
const LOCKED_VISIT_PERIOD_MESSAGE = 'この訪問データは既にロック済みの訪問期間です。';
const OTHER_PRACTITIONER_MESSAGE = 'この受診者はすでに他の施術師にて登録されています。';
const LOCKED_PERIOD_CODE = 'visits_already_is_locked_error';

export function normalizeVisitErrorMessage(
  code: string | undefined,
  message: string,
  lockedPeriodMessage?: string
): string {
  if (lockedPeriodMessage) {
    // Only the code is trusted here: the 訪問入力 text for this error is the same sentence as
    // the 他の施術師 duplicate error, so it cannot be told apart by text.
    if (code === LOCKED_PERIOD_CODE) return lockedPeriodMessage;
    return message.split(LOCKED_VISIT_PERIOD_MESSAGE).join(lockedPeriodMessage);
  }
  if (code === LOCKED_PERIOD_CODE || message.includes(LOCKED_VISIT_PERIOD_MESSAGE)) {
    return message.split(LOCKED_VISIT_PERIOD_MESSAGE).join(OTHER_PRACTITIONER_MESSAGE);
  }
  return message;
}

function resolveHref(item: VisitErrorItem, tenant: string | undefined): string | null {
  if (!tenant || !item.patient_id) return null;
  switch (item.code) {
    case 'visits_data_health_insurance_card_no_warning':
      return TENANT_ROUTES.PATIENTS.EDIT_BY_ID(tenant, item.patient_id);
    case 'visits_data_consents_treatment_term_no_warning1':
      return item.consent_id
        ? TENANT_ROUTES.CONSENTS.EDIT(tenant, item.patient_id, item.consent_id)
        : TENANT_ROUTES.CONSENTS.LIST(tenant, item.patient_id);
    case 'visits_data_consents_treatment_term_no_warning2':
    case 'visits_data_consents_treatment_term_no_warning3':
      return item.consent_id
        ? TENANT_ROUTES.CONSENTS.EDIT(tenant, item.patient_id, item.consent_id)
        : null;
    default:
      return null;
  }
}

function splitMessages(value: string | string[] | null | undefined): string[] {
  if (!value) return [];
  const raw = Array.isArray(value) ? value : value.split(/[,\n]/);
  return raw.map((s) => s.trim()).filter((s) => s !== '' && s !== '0');
}

const VisitErrorMessageList: React.FC<VisitErrorMessageListProps> = ({
  errorItems,
  errorMessages,
  tenant,
  className,
  lockedPeriodMessage,
}) => {
  const items = Array.isArray(errorItems) ? errorItems : [];
  const fallbackMsgs = splitMessages(errorMessages).map((msg) =>
    normalizeVisitErrorMessage(undefined, msg, lockedPeriodMessage)
  );
  // An item's stored text also counts as rendered: once the item is shown under a different
  // wording, the same sentence in error_messages must not come back as an extra line.
  const renderedFromItems = new Set([
    ...items.map((i) => normalizeVisitErrorMessage(i.code, i.message, lockedPeriodMessage)),
    ...items.map((i) => i.message),
  ]);
  const extras = fallbackMsgs.filter((m) => !renderedFromItems.has(m));

  if (items.length === 0 && extras.length === 0) return null;

  return (
    <div className={className ?? 'text-sm divide-y divide-grey-200'}>
      {items.map((item, idx) => {
        const href = resolveHref(item, tenant);
        const message = normalizeVisitErrorMessage(item.code, item.message, lockedPeriodMessage);
        if (href) {
          return (
            <div key={`item-${idx}`} className="py-1">
              <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                {message}
              </a>
            </div>
          );
        }
        return (
          <div key={`item-${idx}`} className={`py-1 ${PLAIN_CLASS}`}>
            {message}
          </div>
        );
      })}
      {extras.map((msg, idx) => (
        <div key={`extra-${idx}`} className={`py-1 ${PLAIN_CLASS}`}>
          {msg}
        </div>
      ))}
    </div>
  );
};

export default VisitErrorMessageList;
